import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Footer() {
  const { t, lang, localizePath } = useLanguage();

  const currentYear = new Date().getFullYear();

  const navLinks = [
    { name: t('nav.udredning'), path: '/udredning' },
    { name: t('nav.adhd'), path: '/adhd' },
    { name: t('nav.autisme'), path: '/autisme' },
    { name: t('nav.psykiatriske_lidelser'), path: '/psykiatriske-lidelser' },
    { name: t('nav.medicinsk_behandling'), path: '/medicinsk-behandling' },
    { name: t('nav.vidensbank'), path: '/vidensbank' },
    { name: t('nav.about'), path: '/om-os' }
  ];

  const introText = lang === 'da'
    ? 'Specialistklinik med fokus på ADHD og autisme. Vi tilbyder professionel, tryg og individuelt tilpasset udredning med høj faglig kvalitet.'
    : 'Specialist clinic focusing on ADHD and autism. We offer professional, safe, and individually tailored assessments of high clinical quality.';

  return (
    <footer className="bg-sand-light text-text-dark/90 border-t border-sand-medium/60 pt-16 pb-8 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        {/* Top Section: 4-Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12 border-b border-sand-medium/40">
          
          {/* Column 1: Logo & Contact Details */}
          <div className="flex flex-col gap-8 lg:col-span-4">
            <Link to={localizePath('/')} className="flex items-center gap-3 group -ml-1">
              <img 
                alt="Nordisk Psykiatri" 
                className="h-10 sm:h-12 w-auto object-contain" 
                src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/nordisk-psykiatri-kompaslogo.png"
              />
            </Link>

            <div className="space-y-4 text-sm text-text-dark/80">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-petrol-medium shrink-0 mt-0.5" />
                <div>
                  <strong className="text-petrol-dark font-semibold block">Vesterbrogade 37, 1. tv</strong>
                  <span className="block">1620 København V</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-petrol-medium shrink-0" />
                <strong>
                  <a href="tel:+4523957707" className="hover:text-petrol-dark transition-colors text-petrol-dark font-semibold">
                    +45 23 95 77 07
                  </a>
                </strong>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-petrol-medium shrink-0" />
                <strong>
                  <a href="mailto:kontakt@nordiskpsykiatri.dk" className="hover:text-petrol-dark transition-colors text-petrol-dark font-semibold">
                    kontakt@nordiskpsykiatri.dk
                  </a>
                </strong>
              </div>
            </div>
          </div>

          {/* Column 2: Navigation Links */}
          <div className="lg:col-span-2">
            <h3 className="font-display text-xs font-bold text-petrol-dark tracking-widest uppercase mb-6">
              Navigation
            </h3>
            <ul className="space-y-3.5">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={localizePath(link.path)}
                    className="text-sm text-text-dark/80 hover:text-petrol-dark transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Praktisk Information */}
          <div className="lg:col-span-3">
            <h3 className="font-display text-xs font-bold text-petrol-dark tracking-widest uppercase mb-6">
              {lang === 'da' ? 'Praktisk information' : 'Practical information'}
            </h3>
            <ul className="space-y-3.5">
              <li>
                <Link
                  to={localizePath('/afbud-og-betaling')}
                  className="text-sm text-text-dark/80 hover:text-petrol-dark transition-colors duration-200"
                >
                  {lang === 'da' ? 'Afbuds- og betalingsbetingelser' : 'Cancellation & payment terms'}
                </Link>
              </li>
              <li>
                <Link
                  to={localizePath('/klagevejledning')}
                  className="text-sm text-text-dark/80 hover:text-petrol-dark transition-colors duration-200"
                >
                  {lang === 'da' ? 'Klagevejledning' : 'Complaints procedure'}
                </Link>
              </li>
              <li>
                <Link
                  to={localizePath('/akut-hjaelp')}
                  className="text-sm text-text-dark/80 hover:text-petrol-dark transition-colors duration-200"
                >
                  {lang === 'da' ? 'Akut hjælp' : 'Emergency help'}
                </Link>
              </li>
              <li>
                <Link
                  to={localizePath('/privatlivspolitik')}
                  className="text-sm text-text-dark/80 hover:text-petrol-dark transition-colors duration-200"
                >
                  {lang === 'da' ? 'Privatlivspolitik (GDPR)' : 'Privacy policy (GDPR)'}
                </Link>
              </li>
              <li>
                <Link
                  to={localizePath('/cookiepolitik')}
                  className="text-sm text-text-dark/80 hover:text-petrol-dark transition-colors duration-200"
                >
                  {lang === 'da' ? 'Cookiepolitik' : 'Cookie policy'}
                </Link>
              </li>
              <li>
                <Link
                  to={localizePath('/vidensbank/mistanke-om-adhd')}
                  className="text-sm text-petrol-dark hover:text-petrol-medium transition-colors duration-200 font-semibold"
                >
                  {lang === 'da' ? 'ASRS-v1.1 ADHD-selvtest' : 'ASRS-v1.1 ADHD self-test'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Opening Hours */}
          <div className="lg:col-span-3">
            <h3 className="font-display text-xs font-bold text-petrol-dark tracking-widest uppercase mb-6">
              {t('contact.hours_title')}
            </h3>
            <ul className="space-y-3.5 text-sm text-text-dark/80">
              <li className="flex flex-col gap-0.5">
                <span className="font-semibold text-text-dark">
                  {lang === 'da' ? 'Mandag - Torsdag: 08:00 - 16:00' : 'Monday - Thursday: 08:00 - 16:00'}
                </span>
              </li>
              <li className="flex flex-col gap-0.5">
                <span className="font-semibold text-text-dark">
                  {lang === 'da' ? 'Søndag: 09:00 - 16:00' : 'Sunday: 09:00 - 16:00'}
                </span>
              </li>
              <li className="flex flex-col gap-0.5">
                <span className="font-semibold text-text-dark/50">
                  {lang === 'da' ? 'Fredag - Lørdag: Lukket' : 'Friday - Saturday: Closed'}
                </span>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Section: Logo Badge & Copyright */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-text-dark/50">
          {/* Small Badge/Logo on left */}
          <div className="flex items-center gap-2">
            <svg
              className="h-6 w-6 text-petrol-medium/40"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M50 20C50 20 54 35 54 50C54 65 50 80 50 80C50 80 46 65 46 50C46 35 50 20 50 20Z"
                fill="currentColor"
              />
              <circle cx="50" cy="20" r="3.5" fill="currentColor" />
            </svg>
            <span className="font-display font-semibold tracking-wider uppercase text-[10px] text-petrol-dark/60">
              Nordisk Psykiatri
            </span>
            <span className="text-text-dark/30">•</span>
            <span className="text-xs text-text-dark/60 font-medium">
              CVR: 46807162
            </span>
          </div>

          {/* Right-aligned copyright and attribution */}
          <div className="flex flex-col sm:items-end gap-1 text-center sm:text-right">
            <p>{lang === 'da' ? `© ${currentYear} Nordisk Psykiatri. Alle rettigheder forbeholdes.` : `© ${currentYear} Nordisk Psykiatri. All rights reserved.`}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
